import i18next from "i18next";
import type { ContractNature, FoaPart, ProcedureType, Regulation, ServiceCategory } from "../../autogen/bff-api";
import { isTiptapContentEmpty } from "../input/TipTap/TipTap";
import { isPartIHealthSocialEducationService } from "../procurement/foaPartDisplay";
import { isNegotiationWithoutNotice } from "../procurement/negotiationWithoutNotice";
import { allowsNegotiation } from "../procurement/procedure";
import { procedureShownAs, procedureTileKey, requiresProcedureJustification } from "../procurement/publicProcurementOptions";
import { currentValueAt } from "./confirmationCardFormatting";
import { MODEL_LANGUAGE } from "./tools";
import type { ToolResult } from "./tools";

type Row = Record<string, unknown>;

export const RULES_NOW =
  "Regelverket, regelverksdelen og prosedyren slik de er lagret nå, står i «storedRules». Nevn regelverket, delen " +
  "og prosedyren bare ut fra det, med navnene «regulationName», «foaPartName» og «procedureName», aldri ut fra det " +
  "du leste om anskaffelsen tidligere, og knytt bare regler og paragrafer til den delen. Står det " +
  "«procedureRequires», sier du det til brukeren. Står det «justificationMissing», sier du hva som mangler før " +
  "anskaffelsen kan publiseres: «ground» betyr at grunnlaget for prosedyren ikke er valgt, «rationale» at " +
  "grunnlaget er valgt, men begrunnelsen for det er tom.";

const SOURCE_NO_LONGER_APPLIES =
  "Kilden kortet oppga for feltene i «sourcesNoLongerApply», viser til et annet regelverk enn det som er lagret, " +
  "så systemet lagret den ikke. " +
  "Si til brukeren at den hjemmelen ikke gjelder lenger, at anskaffelsen nå følger «storedRules.regulationName», " +
  "og hva det krever («storedRules.procedureRequires»).";

const NEGOTIATION_GROUND_REQUIRED = "The procedure may only be used when one of the conditions in FOA § 13-2 is met.";

type StoredRules = {
  regulation: string;
  regulationName: string;
  foaPart: string | null;
  foaPartName: string | null;
  procedureType: string | null;
  procedureName: string | null;
  procedureRequires?: string;
  justificationComplete?: boolean;
  justificationMissing?: "ground" | "rationale";
};

const at = (stored: Row | undefined, read: unknown, path: string[]): unknown => {
  const field = path.join(".");
  return stored && field in stored ? stored[field] : isRow(read) ? currentValueAt(read, path) : undefined;
};

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

export const storedRules = (stored: Row | undefined, read: unknown): StoredRules | undefined => {
  const regulation = text(at(stored, read, ["publicContext", "regulation"])) as Regulation | null;
  if (!regulation) return undefined;
  const foaPart = text(at(stored, read, ["publicContext", "foaPart"])) as FoaPart | null;
  const procedureType = text(at(stored, read, ["publicContext", "procedureType"])) as ProcedureType | null;
  const shownAs = procedureShownAs(procedureType, regulation, foaPart);
  const context = isRow(read) && isRow(read.eventContext) ? read.eventContext : undefined;
  const partIHealthSocialEducation = isPartIHealthSocialEducationService(
    regulation,
    foaPart,
    context?.contractNature as ContractNature | undefined,
    context?.serviceCategory as ServiceCategory | undefined,
  );
  const needsGround =
    allowsNegotiation(procedureType) &&
    !isNegotiationWithoutNotice(procedureType) &&
    requiresProcedureJustification(regulation, partIHealthSocialEducation);
  return {
    regulation,
    regulationName: named(regulation),
    foaPart,
    foaPartName: foaPart ? named(foaPart) : null,
    procedureType,
    procedureName: shownAs ? named(procedureTileKey(shownAs)) : null,
    ...(needsGround
      ? {
          procedureRequires: named(NEGOTIATION_GROUND_REQUIRED),
          ...(context ? justificationState(context.procedureJustification) : {}),
        }
      : {}),
  };
};

const justificationState = (
  justification: unknown,
): Pick<StoredRules, "justificationComplete" | "justificationMissing"> => {
  const ground = isRow(justification) ? text(justification.ground) : null;
  const rationale = isRow(justification) ? text(justification.rationale) : null;
  const missing = !ground ? "ground" : isTiptapContentEmpty(rationale) ? "rationale" : undefined;
  return missing ? { justificationComplete: false, justificationMissing: missing } : { justificationComplete: true };
};

type SourceNoLongerApplies = { field: string; rule: string };

const sourcesNoLongerApply = (body: unknown, notKept: unknown): SourceNoLongerApplies[] => {
  const dropped = Array.isArray(notKept) ? notKept : [];
  const claims = isRow(body) && Array.isArray(body.fieldSources) ? body.fieldSources : [];
  return claims.flatMap((claim) => {
    const source = isRow(claim) && isRow(claim.source) ? claim.source : undefined;
    const rule = source?.kind === "PRODUCT_RULES" ? text(source.rule) : null;
    return rule && isRow(claim) && dropped.includes(claim.path) ? [{ field: String(claim.path), rule }] : [];
  });
};

export const withStoredRulesAfterSave = (saved: ToolResult, body: unknown): ToolResult => {
  if (!saved.ok || !isRow(saved.data)) return saved;
  const { sourceClaimsNotKept, ...procurement } = saved.data;
  const rules = storedRules(undefined, procurement);
  if (!rules) return { ok: true, data: procurement };
  const stale = sourcesNoLongerApply(body, sourceClaimsNotKept);
  return {
    ok: true,
    data: {
      storedRulesNote: stale.length > 0 ? `${RULES_NOW} ${SOURCE_NO_LONGER_APPLIES}` : RULES_NOW,
      storedRules: rules,
      ...(stale.length > 0 ? { sourcesNoLongerApply: stale } : {}),
      ...procurement,
    },
  };
};

const named = (code: string): string => String(i18next.t(code, { lng: MODEL_LANGUAGE }));

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
